/**
 * SUB-AREA COMPONENT - Componente con Sub-Áreas
 * Para componentes que tienen múltiples secciones (Garantías, Municipalidades)
 */

import Component from "./Component.js";

export default class SubAreaComponent extends Component {
  constructor(scene, config) {
    super(scene, config);
    // Los componentes multi siempre tienen tipo 'multi'
    this.type = "multi";
    this.subAreas = config.subAreas || [];
    this.subAreaDamages = new Map(); // Map<subAreaId, Array<damage>>

    // Inicializar map de daños por sub-área
    this.subAreas.forEach((subArea) => {
      this.subAreaDamages.set(subArea.id, []);
    });
  }

  /**
   * Agregar daño a una sub-área específica
   * @param {string} subAreaId - ID de la sub-área
   * @param {object} damage - Objeto de daño
   */
  addDamageToSubArea(subAreaId, damage) {
    if (!this.subAreaDamages.has(subAreaId)) {
      console.warn(`Sub-área ${subAreaId} no existe en ${this.name}`);
      return false;
    }

    const subAreaDamages = this.subAreaDamages.get(subAreaId);
    subAreaDamages.push(damage);
    this.damages.push(damage); // También agregarlo al array general

    this.onDamageAdded(damage);
    return true;
  }

  /**
   * Reparar una sub-área específica
   * @param {string} subAreaId - ID de la sub-área
   * @returns {number} Valor de integridad recuperado
   */
  repairSubArea(subAreaId) {
    if (!this.subAreaDamages.has(subAreaId)) {
      console.warn(`Sub-área ${subAreaId} no existe en ${this.name}`);
      return 0;
    }

    const subAreaDamages = this.subAreaDamages.get(subAreaId);
    const damageCount = subAreaDamages.length;

    if (damageCount === 0) {
      return 0; // No hay daños que reparar
    }

    // Encontrar la sub-área para obtener su valor
    const subArea = this.subAreas.find((sa) => sa.id === subAreaId);
    if (!subArea) {
      return 0;
    }

    // Remover daños del array general
    subAreaDamages.forEach((damage) => {
      const index = this.damages.indexOf(damage);
      if (index !== -1) {
        this.damages.splice(index, 1);
      }
    });

    // Limpiar daños de la sub-área
    this.subAreaDamages.set(subAreaId, []);

    this.onSubAreaRepaired(subAreaId, damageCount);

    return subArea.integrityValue;
  }

  /**
   * Verificar si una sub-área tiene daños
   * @param {string} subAreaId
   * @returns {boolean}
   */
  subAreaHasDamage(subAreaId) {
    if (!this.subAreaDamages.has(subAreaId)) {
      return false;
    }
    return this.subAreaDamages.get(subAreaId).length > 0;
  }

  /**
   * Obtener cantidad de daños en una sub-área
   * @param {string} subAreaId
   * @returns {number}
   */
  getSubAreaDamageCount(subAreaId) {
    if (!this.subAreaDamages.has(subAreaId)) {
      return 0;
    }
    return this.subAreaDamages.get(subAreaId).length;
  }

  /**
   * Reparar todo el componente (todas las sub-áreas)
   * @returns {number} Valor total de integridad recuperado
   */
  repair() {
    // Para componentes con sub-áreas, reparar basado en daños generales
    const damageCount = this.damages.length;

    if (damageCount === 0) {
      return 0; // No hay nada que reparar
    }

    // Limpiar todos los daños
    this.damages = [];

    // Limpiar daños de todas las sub-áreas
    this.subAreaDamages.forEach((damages, subAreaId) => {
      this.subAreaDamages.set(subAreaId, []);
    });

    // Retornar el valor de integridad del componente completo
    // Calcular promedio de las sub-áreas
    const avgIntegrity =
      this.subAreas.reduce((sum, sa) => sum + sa.integrityValue, 0) /
      this.subAreas.length;

    this.onRepaired(damageCount);

    return Math.round(avgIntegrity);
  }

  /**
   * Callback cuando se repara una sub-área
   */
  onSubAreaRepaired(subAreaId, damageCount) {
    console.log(
      `🔧 Sub-área ${subAreaId} de ${this.name} reparada (${damageCount} daños)`
    );
  }

  /**
   * Renderizar componente (placeholder - se implementará después)
   * @param {number} x - Posición X
   * @param {number} y - Posición Y
   */
  render(x, y) {
    // TODO: Implementar renderizado visual de sub-áreas
    console.log(
      `Renderizando ${this.name} con ${this.subAreas.length} sub-áreas en (${x}, ${y})`
    );
  }

  /**
   * Destruir el componente
   */
  destroy() {
    this.subAreaDamages.clear();
    super.destroy();
  }
}
